import { apiRequest, msalInstance } from "src/msalConfig";;

const API_HOST = process.env.REACT_APP_API_HOST;

const getTokenFromMSAL = async (): Promise<string | undefined> => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
    const accessTokenResponse = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: msalInstance.getActiveAccount() ?? undefined, // Explicitly set account
    });
    return accessTokenResponse.accessToken;
  }

  return undefined;
};

async function request(path: string, options?: RequestInit) {
  let token;
  if (localStorage.getItem("msal.idtoken")) {
    token = localStorage.getItem("msal.idtoken");
  } else {
    token = await getTokenFromMSAL();
  }

  options = { ...options };
  options.headers = { Authorization: `Bearer ${token}`, ...options.headers };

  const url = `${API_HOST}${path}`;
  const response = await fetch(url, options);

  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch {
      // Ignore a failure to parse the response
    }

    throw new HTTPError(url, response.status, response.statusText, body);
  }

  return response;
}

// Format an error message based on our standard error response bodies
// Commenting out the getErrorMessage function to avoid showing toast notifications
// const getErrorMessage = (json: any): string => {
//   if (json.message) {
//     return json.message;
//   } else if (json.errors) {
//     const messages: string[] = [];
//     for (const attr in json.errors) {
//       if (json.errors.hasOwnProperty(attr)) {
//         messages.push(`${attr}: ${json.errors[attr]}`);
//       }
//     }
//     return messages.join("\n");
//   } else {
//     return "Error making request";
//   }
// };

// Errors need to be visible in a few different places
// - Toast, so the user has some feedback
// - console, so the developer has some feedback
// - thrown, so the code has some feedback

// Commenting out the toastAndThrow function to avoid showing toast notifications
// const toastAndThrow = (e: Error) => {
//   Toaster.show({
//     message: e.message,
//     intent: "danger",
//     timeout: 0,
//   });
//   console.log(e);
//   throw e;
// };

// Commenting out the responseHandler function to avoid showing toast notifications
// const responseHandler =
//   (url: string, body: object) =>
//   (response: Response): null | Promise<any> => {
//     if (response.status === 204) {
//       return null;
//     }
//     if (response.ok) {
//       return (
//         response
//           .json()
//           // Wrap errors that might occur when parsing the JSON body with a better
//           // message
//           .catch((e: Error) => {
//             throw new RequestError(
//               "Error making request",
//               url,
//               body,
//               response,
//               e
//             );
//           })
//       );
//     }
//     // Response wasn't ok, so let's parse it for any errors
//     return (
//       response
//         .json()
//         // Wrap errors that might occur when parsing the JSON body with a better
//         // message
//         .catch((e: Error) => {
//           throw new RequestError(
//             "Error making request",
//             url,
//             body,
//             response,
//             e
//           );
//         })
//         .then((json: any) => {
//           const errorMessage = getErrorMessage(json);
//           throw new RequestError(errorMessage, url, body, response);
//         })
//     );
//   };

class ApiService {
  public async get(path: string, signal?: RequestInit) {
    if (path) {
      const response = await request(path, signal);
      return await response.json();
    }
    return null;
  }

  public async post<T>(path: string, payload: object = {}): Promise<any> {
    const response = await request(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    return (await response.json()) as T;
  }

  public async delete(path: string, body = {}): Promise<any> {
    const response = await request(path, {
      method: "DELETE",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });

    return (await response.json())
  }

  public async getBlob(path: string): Promise<{ blob: Blob; filename: string | null; contentType: string | null }> {
    const response = await request(path);
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename\s*=\s*"?([^"]+)"?/i);
    const filename = match ? match[1] : null;
    const contentType = response.headers.get("Content-Type");
    return { blob, filename, contentType };
  }

  public async postMultipart<T>(path: string, formData: FormData): Promise<T> {
    // Lets the browser set Content-Type with boundary; do not set it manually.
    const response = await request(path, {
      method: "POST",
      body: formData,
    });
    return (await response.json()) as T;
  }

  public async deleteNoContent(path: string): Promise<void> {
    // For endpoints that return 204 with no body. apiService.delete()
    // calls response.json() which throws on empty bodies.
    await request(path, { method: "DELETE" });
  }

  public async uploadToBlob(blobUrl: string, file: File): Promise<Response> {
    const response = await fetch(blobUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload to blob storage: ${response.statusText}`);
    }

    return response;
  }

  public async getOutputTags(clientId: number): Promise<any> {
    return await this.get(`/clients/${clientId}/output_tags`);
  }

  public async createOutputTag(
    clientId: number,
    name: string
  ): Promise<any> {
     return await this.post(`/clients/${clientId}/output_tags`, { name });
  }

  // Client Dimensions
  public async getClientDimensions(clientId: number): Promise<any> {
    return await this.get(`/clients/${clientId}/client_dimension`);
  }

  public async createClientDimension(clientId: number, name: string): Promise<any> {
    return await this.post(`/clients/${clientId}/client_dimension`, { name });
  }

  // Client Dimension Instances
  public async getClientDimensionInstances(
    clientId: number,
    clientDimensionId: number
  ): Promise<any> {
    return await this.get(
      `/clients/${clientId}/client_dimension/${clientDimensionId}/client_dimension_instance`
    );
  }

  public async createClientDimensionInstance(
    clientId: number,
    clientDimensionId: number,
    name: string
  ): Promise<any> {
    return await this.post(
      `/clients/${clientId}/client_dimension/${clientDimensionId}/client_dimension_instance`,
      { name }
    );
  }

  // Dimension Instances – Bulk Template Download & Upload
  public async getDimensionInstancesBulkTemplate(
    instanceId: number
  ): Promise<{ url: string; generated_file_id: number }> {
    return await this.get(`/instances/${instanceId}/dimensions/bulk_template`);
  }

  public async uploadDimensionInstancesBulk(
    instanceId: number,
    file: File
  ): Promise<{ inserted: number; skipped: number; upload_id: number; blob_name: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return await this.postMultipart<{ inserted: number; skipped: number; upload_id: number; blob_name: string }>(
      `/instances/${instanceId}/dimensions/bulk_upload`,
      formData
    );
  }

  // Nodes – Bulk Template Download & Upload
  public async getNodesBulkTemplate(
    instanceId: number
  ): Promise<{ url: string; generated_file_id: number }> {
    return await this.get(`/instances/${instanceId}/nodes/bulk_template`);
  }

  public async uploadNodesBulk(
    instanceId: number,
    file: File
  ): Promise<{ inserted: number; skipped: number; upload_id: number; blob_name: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return await this.postMultipart<{ inserted: number; skipped: number; upload_id: number; blob_name: string }>(
      `/instances/${instanceId}/nodes/bulk_upload`,
      formData
    );
  }

  // Client Attributes
  public async getClientAttributes(clientId: number): Promise<any> {
    return await this.get(`/clients/${clientId}/client_attribute`);
  }

  public async createClientAttribute(clientId: number, name: string): Promise<any> {
    return await this.post(`/clients/${clientId}/client_attribute`, { name });
  }

  // Client Attribute Instances
  public async getClientAttributeInstances(
    clientId: number,
    clientAttributeId: number
  ): Promise<any> {
    return await this.get(
      `/clients/${clientId}/client_attribute/${clientAttributeId}/client_attribute_instance`
    );
  }

  public async createClientAttributeInstance(
    clientId: number,
    clientAttributeId: number,
    name: string
  ): Promise<any> {
    return await this.post(
      `/clients/${clientId}/client_attribute/${clientAttributeId}/client_attribute_instance`,
      { name }
    );
  }

  // Attribute Mappings
  public async getAttributeMappings(
    clientId: number,
    dimensionId: number
  ): Promise<any> {
    return await this.get(
      `/clients/${clientId}/client_dimension/${dimensionId}/attribute_mapping`
    );
  }

  public async saveAttributeMappings(
    clientId: number,
    dimensionId: number,
    clientAttributeIds: number[]
  ): Promise<any> {
    return await this.post(
      `/clients/${clientId}/client_dimension/${dimensionId}/attribute_mapping`,
      { client_attribute_ids: clientAttributeIds }
    );
  }

  // Instance Attribute Mappings
  public async getInstanceAttributeMappings(
    clientId: number,
    dimensionId: number,
    dimensionInstanceId: number
  ): Promise<any> {
    return await this.get(
      `/clients/${clientId}/client_dimension/${dimensionId}/client_dimension_instance/${dimensionInstanceId}/attribute_instance_mapping`
    );
  }

  public async saveInstanceAttributeMappings(
    clientId: number,
    dimensionId: number,
    dimensionInstanceId: number,
    clientAttributeIds: number[]
  ): Promise<any> {
    return await this.post(
      `/clients/${clientId}/client_dimension/${dimensionId}/client_dimension_instance/${dimensionInstanceId}/attribute_instance_mapping`,
      { client_attribute_instance_ids: clientAttributeIds }
    );
  }
}

const apiService = new ApiService();
export default apiService;

export class HTTPError extends Error {
  constructor(
    public url: string,
    public status: number,
    public statusText: string,
    public body?: any
  ) {
    super(`${status}: ${statusText}`);
  }
}